import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Stepper, Step, StepButton, StepLabel } from '@material-ui/core';
import { NavigateBefore, NavigateNext, Check } from '@material-ui/icons';
import styles from './WizardCompactToolbar.module.scss';
import IntlMessages from 'util/IntlMessages';

const WizardCompactToolbar = ({
  // Navigation props
  onPrev,
  onNext,
  onComplete,
  canGoPrev,
  canGoNext,
  isLastStep,
  loading,

  // Progress props
  steps,
  currentStep,
  onStepClick,
}) => {
  const isStepCompleted = (stepIndex) => stepIndex < currentStep;
  const isStepAccessible = (stepIndex) => stepIndex <= currentStep || isStepCompleted(stepIndex);

  return (
    <Box className={styles.root}>
      {/* Left: Back Button */}
      <Box className={styles.leftSection}>
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          onClick={onPrev}
          disabled={!canGoPrev || loading}
          className={styles.navButton}
        >
          <IntlMessages id="appModule.back" />
        </Button>
      </Box>

      {/* Middle: Stepper */}
      <Box className={styles.middleSection}>
        <Stepper activeStep={currentStep} className={styles.stepper} alternativeLabel>
          {steps.map((step, index) => {
            const completed = isStepCompleted(index);
            const accessible = isStepAccessible(index);

            return (
              <Step key={step.id} completed={completed}>
                {accessible ? (
                  <StepButton
                    onClick={() => onStepClick && onStepClick(index)}
                    className={styles.stepButton}
                  >
                    <StepLabel className={styles.stepLabel}>
                      <span className={styles.stepLabelText}>{step.title}</span>
                    </StepLabel>
                  </StepButton>
                ) : (
                  <StepLabel className={styles.stepLabel}>
                    <span className={styles.stepLabelText}>{step.title}</span>
                  </StepLabel>
                )}
              </Step>
            );
          })}
        </Stepper>

        {/* Portal Target for Embedded Steps (e.g. Configuration dots) */}
        <Box id="wizard-navigation-center" className={styles.portalTarget} />
      </Box>

      {/* Right: Next/Complete Button */}
      <Box className={styles.rightSection}>
        {!isLastStep && (
          <Button
            variant="contained"
            color="primary"
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NavigateNext />}
            onClick={onNext}
            disabled={!canGoNext || loading}
            className={styles.navButton}
          >
            <IntlMessages id="appModule.next" />
          </Button>
        )}

        {isLastStep && onComplete && (
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Check />}
            onClick={onComplete}
            disabled={loading}
            className={styles.navButton}
          >
            <IntlMessages id="wizard.button.finish" />
          </Button>
        )}

        {isLastStep && !onComplete && (
          <Button
            variant="contained"
            color="primary"
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NavigateNext />}
            onClick={onNext}
            disabled={!canGoNext || loading}
            className={styles.navButton}
          >
            <IntlMessages id="appModule.next" />
          </Button>
        )}
      </Box>
    </Box>
  );
};

WizardCompactToolbar.propTypes = {
  // Navigation
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  canGoPrev: PropTypes.bool.isRequired,
  canGoNext: PropTypes.bool.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  loading: PropTypes.bool,

  // Progress
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  isEmbeddedStep: PropTypes.bool,
};

WizardCompactToolbar.defaultProps = {
  loading: false,
  onComplete: null,
  onStepClick: null,
  isEmbeddedStep: false,
};

export default WizardCompactToolbar;
